import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  AwardLetterStatusDto,
  SeV2QuestionKind,
  SeV2QuestionStatus,
  SourcingEventStatus,
  useGetBuyerSeV2QuestionsQuery,
  useGetAwardLettersQuery,
  useGetEvaluationQuery,
  useListBidV2SForEventQuery,
  useListParticipationRequestsForBuyerQuery,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import {
  isSchemeProcedure,
  isTwoStageProcedure,
} from "../../../sourcing-events/buyer/wizard/utils/procedure";
import { requireBooleanEnvVar } from "../../../../config";
import { useCurrentMoment } from "../../../sourcing-events/portal/utils/useCurrentMoment";
import { useAwardSelection } from "../award/AwardSelectionContext";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import type { EventTab } from "../EventTabs";
import { panelReadsBids, type NowPanelInput } from "./deriveNowPanel";

const EVALUATING_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.Evaluated,
]);

const AWARDED_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
]);

export const useNowPanelData = (event: SourcingEventV2Dto, activeTab: EventTab): NowPanelInput => {
  const eventId = event.id;
  const { rounds } = useRoundSelection();
  const { winnerIds } = useAwardSelection();
  const now = useCurrentMoment().valueOf();

  const procedureType = event.eventContext.type === "Public" ? event.eventContext.procedureType : null;
  const collectsRequests = isTwoStageProcedure(procedureType) || isSchemeProcedure(procedureType);

  const { data: questionsData } = useGetBuyerSeV2QuestionsQuery(
    event.status === SourcingEventStatus.Published ? { eventId } : skipToken,
  );
  const { data: requestsData } = useListParticipationRequestsForBuyerQuery(
    collectsRequests ? { eventId } : skipToken,
  );
  const { data: bidsData, isSuccess: bidsLoaded } = useListBidV2SForEventQuery(
    panelReadsBids(event.status) ? { eventId } : skipToken,
  );
  const { data: evaluation } = useGetEvaluationQuery(
    EVALUATING_STATUSES.has(event.status) ? { eventId } : skipToken,
  );
  const { data: awardLetters } = useGetAwardLettersQuery(
    AWARDED_STATUSES.has(event.status) ? { eventId } : skipToken,
  );

  const unansweredQuestions = (questionsData?.questions ?? []).filter(
    (question) =>
      question.kind === SeV2QuestionKind.Question &&
      question.status === SeV2QuestionStatus.Unanswered,
  ).length;

  return {
    event,
    now,
    unansweredQuestions,
    participationRequests: requestsData ?? null,
    bids: bidsData?.bids ?? [],
    bidsLoaded,
    selectedWinnerCount: winnerIds.length,
    evaluation: evaluation ?? null,
    rounds,
    awardLettersSent: awardLetters == null
      ? null
      : awardLetters.letters.every((letter) => letter.status === AwardLetterStatusDto.Sent),
    awardLettersApproved: awardLetters == null
      ? null
      : awardLetters.letters.length > 0 &&
        awardLetters.letters.every((letter) => letter.status === AwardLetterStatusDto.Approved),
    activeTab,
    focusModeEnabled: requireBooleanEnvVar("VITE_ENABLE_FOCUS_MODE"),
  };
};
